import { useState, useCallback } from 'react';
import { UploadedFile } from '../../hooks/file-uploads/types';
import { isDescendantOfFolder } from '../utils/isDescendantOfFolder';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';

type UseFileDragDropReturn = {
  draggedItem: UploadedFile | null;
  setDraggedItem: (item: UploadedFile | null) => void;
  isDraggedOver: string | null;
  setIsDraggedOver: (id: string | null) => void;
  handleMoveFile: (file: UploadedFile, parentId: string | null | undefined) => Promise<void>;
  allFiles: UploadedFile[];
  handleDragStart: (item: UploadedFile) => void;
  handleDragEnd: () => void;
  handleDragOver: (event: React.DragEvent, targetId: string) => void;
  handleDragLeave: () => void;
  handleDrop: (event: React.DragEvent, targetId: string | null) => Promise<void>;
  canDropOnTarget: (targetId: string | null) => boolean;
  isDragging: (itemId: string) => boolean;
  isDropTarget: (itemId: string) => boolean;
};

type UseFileDragDropProps = {
  allFiles: UploadedFile[];
  handleMoveFile: (file: UploadedFile, parentId: string | null | undefined) => Promise<void>;
  clientId: string;
};

export function useFileDragDrop({
  allFiles,
  handleMoveFile,
  clientId,
}: UseFileDragDropProps): UseFileDragDropReturn {
  const [draggedItem, setDraggedItem] = useState<UploadedFile | null>(null);
  const [isDraggedOver, setIsDraggedOver] = useState<string | null>(null);

  const handleDragStart = useCallback((item: UploadedFile) => {
    setDraggedItem(item);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedItem(null);
    setIsDraggedOver(null);
  }, []);

  const canDropOnTarget = useCallback(
    (targetId: string | null) => {
      if (!draggedItem) return false;

      if (targetId === null) return true;

      const targetFolder = allFiles.find(file => file.id === targetId && file.isFolder);
      if (!targetFolder) return false;

      if (isDescendantOfFolder(draggedItem.id, targetId, allFiles)) {
        return false;
      }

      return true;
    },
    [draggedItem, allFiles],
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent, targetId: string) => {
      event.preventDefault();

      if (canDropOnTarget(targetId)) {
        setIsDraggedOver(targetId);
      }
    },
    [canDropOnTarget],
  );

  const handleDragLeave = useCallback(() => {
    setIsDraggedOver(null);
  }, []);

  const handleDrop = useCallback(
    async (event: React.DragEvent, targetId: string | null) => {
      event.preventDefault();
      setIsDraggedOver(null);

      if (!draggedItem || !canDropOnTarget(targetId)) {
        return;
      }

      try {
        await handleMoveFile(draggedItem, targetId);
      } catch (error) {
        notifyAndLogError(
          'Failed to move file',
          'An error occurred while trying to move the file. Please try again later.',
          error,
          { clientId, fileId: draggedItem.id, targetId },
        );
      }
      setDraggedItem(null);
    },
    [draggedItem, canDropOnTarget, handleMoveFile, clientId],
  );

  const isDragging = useCallback(
    (itemId: string) => {
      return draggedItem?.id === itemId;
    },
    [draggedItem],
  );

  const isDropTarget = useCallback(
    (itemId: string) => {
      return isDraggedOver === itemId;
    },
    [isDraggedOver],
  );

  return {
    draggedItem,
    setDraggedItem,
    isDraggedOver,
    setIsDraggedOver,
    handleMoveFile,
    allFiles,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    canDropOnTarget,
    isDragging,
    isDropTarget,
  };
}
